import React, { useState, useEffect, useContext } from 'react';
import { IoChevronDown, IoChevronUp } from 'react-icons/io5';
import { AppContext } from '../../context/appContext';
import { generateAppLink, parseAppLink, getLinkDescription } from '../../utils/linkHelpers';
import CategoryPickerModal from './CategoryPickerModal';
import ContentPickerModal from './ContentPickerModal';
import BookPickerModal from './BookPickerModal';
import BibleChapterPicker from './BibleChapterPicker';

/**
 * InternalLinkBuilder - Inline expandable link builder component
 * @param {string} value - Current link value
 * @param {function} onChange - Callback when link changes (link, metadata)
 * @param {string} name - Field name for form handling
 * @param {string} error - Validation error message
 */
const InternalLinkBuilder = ({ value = '', onChange, name = 'link', error = '' }) => {
  const { t, isRTL } = useContext(AppContext);

  const [isExpanded, setIsExpanded] = useState(false);
  const [linkType, setLinkType] = useState('internal'); // 'internal' | 'external'
  const [navigationType, setNavigationType] = useState(''); // 'mainPage', 'audioCategory', etc.
  const [externalUrl, setExternalUrl] = useState('');
  const [selectedData, setSelectedData] = useState({});
  const [linkMetadata, setLinkMetadata] = useState({});

  // Modal states
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showContentModal, setShowContentModal] = useState(false);
  const [showBookModal, setShowBookModal] = useState(false);
  const [modalType, setModalType] = useState(''); // 'audio', 'video', 'book'

  // Initialize from existing value
  useEffect(() => {
    if (value) {
      if (value.startsWith('http://') || value.startsWith('https://')) {
        setLinkType('external');
        setExternalUrl(value);
      } else if (value.startsWith('app://')) {
        setLinkType('internal');
        const parsed = parseAppLink(value);
        if (parsed.isValid) {
          setNavigationType(parsed.type);
          setSelectedData(parsed.params);
        }
      }
    }
  }, []);

  const handleLinkTypeChange = (type) => {
    setLinkType(type);
    setNavigationType('');
    setSelectedData({});
    setLinkMetadata({});
    setExternalUrl('');

    // Clear the link value in parent component
    onChange('', {});

    if (type === 'external') {
      setIsExpanded(true);
    }
  };

  const handleNavigationTypeChange = (type) => {
    setNavigationType(type);
    setSelectedData({});
    setLinkMetadata({});

    // Clear the previous link when changing navigation type
    onChange('', {});

    // Open appropriate picker
    if (type === 'mainPage') {
      // Main pages don't need a modal, just a dropdown
    } else if (['audioCategory', 'videoCategory', 'bookCategory', 'miscCategory', 'series'].includes(type)) {
      // 'series'.replace('Category','') === 'series' → CategoryPickerModal type='series'
      const contentType = type.replace('Category', '');
      setModalType(contentType);
      setShowCategoryModal(true);
    } else if (['audioContent', 'videoContent', 'miscContent'].includes(type)) {
      const contentType = type.replace('Content', '');
      setModalType(contentType);
      setShowContentModal(true);
    } else if (type === 'book') {
      setShowBookModal(true);
    } else if (type === 'bible') {
      // Bible uses inline picker
    }
  };

  const handleMainPageSelect = (page) => {
    const link = generateAppLink('mainPage', { page });
    const description = getLinkDescription(link);
    onChange(link, { mainPage: page, description });
    setSelectedData({ page });
    setLinkMetadata({ description });
  };

  const handleCategorySelect = (data) => {
    const link = generateAppLink(navigationType, { categoryId: data.categoryId });
    const description = getLinkDescription(link, { categoryTitle: data.categoryTitle });
    onChange(link, { categoryTitle: data.categoryTitle, description });
    setSelectedData({ categoryId: data.categoryId });
    setLinkMetadata({ categoryTitle: data.categoryTitle, description });
  };

  const handleContentSelect = (data) => {
    // Only video content is keyed by videoId; audio and misc use contentId.
    const params = navigationType === 'videoContent'
      ? { videoId: data.contentId }
      : { contentId: data.contentId };
    const link = generateAppLink(navigationType, params);
    const description = getLinkDescription(link, { contentTitle: data.contentTitle });
    onChange(link, { contentTitle: data.contentTitle, description });
    setSelectedData(params);
    setLinkMetadata({ contentTitle: data.contentTitle, description });
  };

  const handleBookSelect = (data) => {
    const link = generateAppLink('book', { bookId: data.bookId });
    const description = getLinkDescription(link, { bookTitle: data.bookTitle });
    onChange(link, { bookTitle: data.bookTitle, description });
    setSelectedData({ bookId: data.bookId });
    setLinkMetadata({ bookTitle: data.bookTitle, description });
  };

  const handleBibleSelect = (data) => {
    const link = generateAppLink('bible', {
      languageId: data.languageId,
      bookCode: data.bookCode,
      chapter: data.chapter
    });
    const description = getLinkDescription(link, data.metadata);
    onChange(link, { ...data.metadata, description });
    setSelectedData({ languageId: data.languageId, bookCode: data.bookCode, chapter: data.chapter });
    setLinkMetadata({ ...data.metadata, description });
  };

  const handleExternalUrlChange = (e) => {
    const url = e.target.value;
    setExternalUrl(url);
    onChange(url, { external: true });
  };

  const getPreviewText = () => {
    if (!value) return t('common.noLinkSet') || 'No link set';
    if (linkType === 'external') return value;
    return linkMetadata.description || getLinkDescription(value);
  };

  return (
    <div className="space-y-2">
      {/* Collapsed View */}
      {!isExpanded && (
        <div
          onClick={() => setIsExpanded(true)}
          className="p-3 border rounded-lg cursor-pointer hover:bg-base-200 transition-colors flex justify-between items-center"
          dir={isRTL ? 'rtl' : 'ltr'}
        >
          <div className="flex-1">
            <div className="text-sm text-gray-500 mb-1">
              {t('common.navigationLink') || 'Navigation Link'}
            </div>
            <div className="font-medium">{getPreviewText()}</div>
            {error && <div className="text-sm text-error mt-1">{error}</div>}
          </div>
          <IoChevronDown size={20} />
        </div>
      )}

      {/* Expanded View */}
      {isExpanded && (
        <div className="p-4 border rounded-lg space-y-4" dir={isRTL ? 'rtl' : 'ltr'}>
          {/* Collapse Button */}
          <div className="flex justify-between items-center">
            <h4 className="font-semibold">{t('common.configureLink') || 'Configure Link'}</h4>
            <button
              type="button"
              onClick={() => setIsExpanded(false)}
              className="btn btn-sm btn-ghost"
            >
              <IoChevronUp size={20} />
            </button>
          </div>

          {/* Link Type Selection */}
          <div>
            <label className="label">
              <span className="label-text">{t('common.linkType') || 'Link Type'}</span>
            </label>
            <div className="flex gap-4">
              <label className="cursor-pointer flex items-center gap-2">
                <input
                  type="radio"
                  className="radio"
                  checked={linkType === 'internal'}
                  onChange={() => handleLinkTypeChange('internal')}
                />
                <span>{t('common.internalAppLink') || 'Internal App Link'}</span>
              </label>
              <label className="cursor-pointer flex items-center gap-2">
                <input
                  type="radio"
                  className="radio"
                  checked={linkType === 'external'}
                  onChange={() => handleLinkTypeChange('external')}
                />
                <span>{t('common.externalWebsite') || 'External Website'}</span>
              </label>
            </div>
          </div>

          {/* Internal Link Configuration */}
          {linkType === 'internal' && (
            <>
              {/* Navigation Type Selector */}
              <div>
                <label className="label">
                  <span className="label-text">{t('common.navigateTo') || 'Navigate To'}</span>
                </label>
                <select
                  className="select select-bordered w-full"
                  value={navigationType}
                  onChange={(e) => handleNavigationTypeChange(e.target.value)}
                >
                  <option value="">{t('common.selectNavigationType') || '-- Select Navigation Type --'}</option>
                  <option value="mainPage">{t('common.mainPages') || 'Main Pages'}</option>
                  <option value="audioCategory">{t('common.audioCategory') || 'Audio Category'}</option>
                  <option value="videoCategory">{t('common.videoCategory') || 'Video Category'}</option>
                  <option value="series">{t('common.series') || 'Series'}</option>
                  <option value="bookCategory">{t('common.bookCategory') || 'Book Category'}</option>
                  <option value="miscCategory">{t('common.miscCategory') || 'More (بیشتر) Category'}</option>
                  <option value="audioContent">{t('common.specificAudioContent') || 'Specific Audio Content'}</option>
                  <option value="videoContent">{t('common.specificVideoContent') || 'Specific Video Content'}</option>
                  <option value="miscContent">{t('common.specificMiscContent') || 'Specific More (بیشتر) Item'}</option>
                  <option value="book">{t('common.specificBook') || 'Specific Book'}</option>
                  <option value="bible">{t('common.bibleChapter') || 'Bible Chapter'}</option>
                </select>
              </div>

              {/* Main Page Selector */}
              {navigationType === 'mainPage' && (
                <div>
                  <label className="label">
                    <span className="label-text">{t('common.selectPage') || 'Select Page'}</span>
                  </label>
                  <select
                    className="select select-bordered w-full"
                    value={selectedData.page || ''}
                    onChange={(e) => handleMainPageSelect(e.target.value)}
                  >
                    <option value="">{t('common.choosePage') || '-- Choose Page --'}</option>
                    <option value="videos">{t('common.videosMain') || 'Videos Main'}</option>
                    {/* NOTE: app://audios opens the Home tab, not an audio list — see deep_link_handler.dart. */}
                    <option value="audios">{t('common.audiosMain') || 'Home (legacy \'Audios\')'}</option>
                    <option value="books">{t('common.booksMain') || 'Books Main'}</option>
                    <option value="bible">{t('common.bibleMain') || 'Bible Main'}</option>
                    <option value="home">{t('common.homeMain') || 'Home'}</option>
                    <option value="search">{t('common.searchMain') || 'Search'}</option>
                    <option value="worship">{t('common.worshipMain') || 'Worship'}</option>
                    <option value="more">{t('common.moreMain') || 'More (بیشتر)'}</option>
                    <option value="aichat">{t('common.aiChatMain') || 'AI Chat'}</option>
                  </select>
                </div>
              )}

              {/* Bible Chapter Picker */}
              {navigationType === 'bible' && (
                <BibleChapterPicker
                  onSelect={handleBibleSelect}
                  initialValue={selectedData}
                />
              )}

              {/* Preview */}
              {value && value.startsWith('app://') && navigationType && Object.keys(selectedData).length > 0 && (
                <div className="p-3 bg-base-200 rounded-lg">
                  <div className="text-sm text-gray-500 mb-1">
                    {t('common.preview') || 'Preview'}
                  </div>
                  <div className="font-mono text-sm break-all">{value}</div>
                  <div className="text-sm mt-2">{getPreviewText()}</div>
                </div>
              )}
            </>
          )}

          {/* External URL Input */}
          {linkType === 'external' && (
            <div>
              <label className="label">
                <span className="label-text">{t('common.websiteUrl') || 'Website URL'}</span>
              </label>
              <input
                type="url"
                className="input input-bordered w-full"
                placeholder="https://example.com"
                value={externalUrl}
                onChange={handleExternalUrlChange}
              />
            </div>
          )}

          {error && <div className="text-sm text-error">{error}</div>}
        </div>
      )}

      {/* Modals */}
      <CategoryPickerModal
        type={modalType}
        isOpen={showCategoryModal}
        onClose={() => setShowCategoryModal(false)}
        onSelect={handleCategorySelect}
      />

      <ContentPickerModal
        type={modalType}
        isOpen={showContentModal}
        onClose={() => setShowContentModal(false)}
        onSelect={handleContentSelect}
      />

      <BookPickerModal
        isOpen={showBookModal}
        onClose={() => setShowBookModal(false)}
        onSelect={handleBookSelect}
      />
    </div>
  );
};

export default InternalLinkBuilder;
